import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../utils/api';
import { translations } from '../data/translations';

// Static fallback for the FAQ — guarantees items are visible even when the
// backend is unreachable or runs an older (pre-bilingual) schema.
const getStaticFaqs = () => {
  const en = translations.en.faq.questions;
  const el = translations.el.faq.questions;
  return en.map((item, i) => ({
    id: `static-faq-${i}`,
    question_en: item.q,
    question_el: el[i]?.q ?? item.q,
    answer_en: item.a,
    answer_el: el[i]?.a ?? item.a,
    order: i + 1
  }));
};

const isValidBilingualFaq = (f) =>
  f && typeof f === 'object' && 'question_en' in f && 'answer_en' in f;

const FAQSection = () => {
  const { t, language } = useLanguage();
  const [items, setItems] = useState(getStaticFaqs);

  useEffect(() => {
    let active = true;
    authFetch('/api/faq')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data) && data.length > 0 && data.every(isValidBilingualFaq)) {
          setItems(data);
        }
      })
      .catch(() => { /* keep static fallback */ });
    return () => { active = false; };
  }, []);

  return (
    <section className="bg-gradient-to-b from-[#050811] to-[#0a0e27] py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.faq.title}
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {items.map((faq) => {
            const q = faq[`question_${language}`] ?? faq.question_en;
            const a = faq[`answer_${language}`] ?? faq.answer_en;
            return (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                data-testid={`faq-item-${faq.id}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg px-6 hover:border-cyan-500/30 transition-colors"
              >
                <AccordionTrigger className="text-white hover:text-cyan-400 text-left">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70">
                  {a}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
